import React from "react";
import {
  render,
  screen,
  fireEvent,
  waitFor,
  act,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import { Tabs, TabPanel } from "@mui/joy";
import { CanvasTabHeaders, CanvasTabMoveContext } from "./CanvasTabHeaders";
const panels = [
  { id: "a", label: "Alpha" },
  { id: "b", label: "Beta" },
  { id: "c", label: "Gamma" },
];
function Header({
  move,
  disabled = false,
  order = panels,
}: {
  move: jest.Mock;
  disabled?: boolean;
  order?: typeof panels;
}) {
  return (
    <CanvasTabMoveContext.Provider value={{ move, disabled }}>
      <Tabs defaultValue="b">
        <CanvasTabHeaders ownerId="owner" panels={order} />
        {order.map((p) => (
          <TabPanel key={p.id} value={p.id}>
            {p.label} content
          </TabPanel>
        ))}
      </Tabs>
    </CanvasTabMoveContext.Provider>
  );
}
beforeEach(() => {
  class TestPointerEvent extends MouseEvent {
    readonly pointerId: number;
    readonly isPrimary: boolean;
    constructor(type: string, props: PointerEventInit) {
      super(type, props);
      this.pointerId = props.pointerId ?? 1;
      this.isPrimary = props.isPrimary ?? true;
    }
  }
  window.PointerEvent = TestPointerEvent as typeof PointerEvent;
  jest
    .spyOn(HTMLElement.prototype, "getBoundingClientRect")
    .mockImplementation(function (this: HTMLElement) {
      const label = this.textContent || "";
      const index = label.includes("Gamma")
        ? 2
        : label.includes("Beta")
          ? 1
          : 0;
      return {
        x: index * 120,
        y: 0,
        left: index * 120,
        top: 0,
        right: index * 120 + 100,
        bottom: 40,
        width: 100,
        height: 40,
        toJSON: () => ({}),
      };
    });
});
afterAll(() => jest.restoreAllMocks());
it("keyboard drag moves a tab, with the active tab identity unchanged", async () => {
  const move = jest.fn().mockResolvedValue(undefined);
  const { rerender } = render(<Header move={move} />);
  const handle = screen.getByRole("button", { name: "Reorder Alpha" });
  act(() => handle.focus());
  fireEvent.keyDown(handle, { code: "Space", key: " " });
  await waitFor(() => expect(handle).toHaveAttribute("aria-pressed", "true"));
  fireEvent.keyDown(document, { code: "ArrowRight", key: "ArrowRight" });
  expect(await screen.findByText(/over droppable area b/i)).toBeInTheDocument();
  fireEvent.keyDown(document, { code: "Space", key: " " });
  await waitFor(() => expect(move).toHaveBeenCalledWith("owner", "a", 1));
  rerender(<Header move={move} order={[panels[1], panels[0], panels[2]]} />);
  expect(screen.getByRole("tab", { name: /Beta/ })).toHaveAttribute(
    "aria-selected",
    "true",
  );
});
it("pointer drag calls the move without moving the enclosing container", async () => {
  const move = jest.fn().mockResolvedValue(undefined);
  render(<Header move={move} />);
  const handle = screen.getByRole("button", { name: "Reorder Alpha" });
  fireEvent.pointerDown(handle, {
    button: 0,
    isPrimary: true,
    pointerId: 1,
    clientX: 50,
    clientY: 20,
  });
  fireEvent.pointerMove(document, { pointerId: 1, clientX: 60, clientY: 20 });
  await waitFor(() => expect(handle).toHaveAttribute("aria-pressed", "true"));
  fireEvent.pointerMove(document, { pointerId: 1, clientX: 170, clientY: 20 });
  expect(await screen.findByText(/over droppable area b/i)).toBeInTheDocument();
  fireEvent.pointerUp(document, { pointerId: 1, clientX: 170, clientY: 20 });
  await waitFor(() => expect(move).toHaveBeenCalledWith("owner", "a", 1));
});
it("disabled headers do not start a move", () => {
  const move = jest.fn();
  render(<Header move={move} disabled />);
  const handle = screen.getByRole("button", { name: "Reorder Alpha" });
  expect(handle).toHaveAttribute("aria-disabled", "true");
  fireEvent.keyDown(handle, { code: "Space" });
  fireEvent.keyDown(document, { code: "ArrowRight" });
  fireEvent.keyDown(document, { code: "Space" });
  expect(move).not.toHaveBeenCalled();
});
